// Adapted from https://github.com/graphql/graphql-js/blob/main/src/language/printer.ts
import { ASTNode, visit } from 'graphql';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { printBlockString } from 'graphql/language/blockString';

type ASTReducer<R> = {
  readonly [NodeT in ASTNode as NodeT['kind']]?: ASTReducerFn<NodeT, R>;
};

type ASTReducerFn<TReducedNode extends ASTNode, R> = (
  /** The current node being visiting. */
  node: { [K in keyof TReducedNode]: ReducedField<TReducedNode[K], R> },
  /** The index or key to this node from the parent node or Array. */
  key: string | number | undefined,
  /** The parent immediately above this node, which may be an Array. */
  parent: ASTNode | ReadonlyArray<ASTNode> | undefined,
  /** The key path to get to this node from the root node. */
  path: ReadonlyArray<string | number>,
  /**
   * All nodes and Arrays visited before reaching parent of this node.
   * These correspond to array indices in `path`.
   * Note: ancestors includes arrays which contain the parent of visited node.
   */
  ancestors: ReadonlyArray<ASTNode | ReadonlyArray<ASTNode>>,
) => R;

type ReducedField<T, R> = T extends ASTNode
  ? R
  : T extends ReadonlyArray<ASTNode>
    ? ReadonlyArray<R>
    : T;

export const expandedPrint = (ast: ASTNode): string =>
  visit(ast, { leave: printDocASTReducer });

const MAX_LINE_LENGTH = 80;

// ? Note: Because the types for visit in graphqljs v15 are incorrect and don't
// ? support reducers we need to type this ourselves and pass any to prevent any
// ? type issues. After we update graphqljs to v16 we should fix this
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const printDocASTReducer: ASTReducer<any> = {
  Name: (node) => node.value,
  Variable: (node) => `$${node.name}`,

  // Document

  Document: (node) => join(node.definitions, '\n\n'),

  OperationDefinition: (node) => {
    const varDefs = wrap('(', join(node.variableDefinitions, ', '), ')');
    const prefix = join(
      [node.operation, join([node.name, varDefs]), join(node.directives, ' ')],
      ' ',
    );

    // Anonymous queries with no directives or variable definitions can use
    // the query short form.
    return (prefix === 'query' ? '' : `${prefix} `) + node.selectionSet;
  },

  VariableDefinition: ({ variable, type, defaultValue, directives }) =>
    `${variable}: ${type}${wrap(' = ', defaultValue)}${wrap(
      ' ',
      join(directives, ' '),
    )}`,
  SelectionSet: ({ selections }) => block(selections),

  Field: ({ alias, name, arguments: args, directives, selectionSet }) => {
    const prefix = join([wrap('', alias, ': '), name], '');
    let argsLine = prefix + wrap('(', join(args, ', '), ')');

    if (argsLine.length > MAX_LINE_LENGTH) {
      argsLine = prefix + wrap('(\n', indent(join(args, '\n')), '\n)');
    }

    return join([
      argsLine,
      wrap(' ', join(directives, ' ')),
      wrap(' ', selectionSet),
    ]);
  },
  Argument: ({ name, value }) => `${name}: ${value}`,

  // Fragments

  FragmentSpread: ({ name, directives }) =>
    `...${name}${wrap(' ', join(directives, ' '))}`,

  InlineFragment: ({ typeCondition, directives, selectionSet }) =>
    join(
      ['...', wrap('on ', typeCondition), join(directives, ' '), selectionSet],
      ' ',
    ),

  FragmentDefinition: ({
    name,
    typeCondition,
    variableDefinitions,
    directives,
    selectionSet,
  }) =>
    // Note: fragment variable definitions are experimental and may be changed
    // or removed in the future.
    `fragment ${name}${wrap('(', join(variableDefinitions, ', '), ')')} ` +
    `on ${typeCondition} ${wrap(
      '',
      join(directives, ' '),
      ' ',
    )}${selectionSet}`,

  // Value

  IntValue: ({ value }) => value,
  FloatValue: ({ value }) => value,
  StringValue: ({ value, block: isBlockString }, key) =>
    isBlockString
      ? printBlockString(value, key === 'description' ? '' : '  ')
      : JSON.stringify(value),
  BooleanValue: ({ value }) => (value ? 'true' : 'false'),
  NullValue: () => 'null',
  EnumValue: ({ value }) => value,
  ListValue: ({ values }) => {
    const valuesLine = `[${join(values, ', ')}]`;

    if (valuesLine.length > MAX_LINE_LENGTH) {
      return `[\n${indent(join(values, '\n'))}\n]`;
    }
    return valuesLine;
  },

  ObjectValue: ({ fields }) => {
    const fieldsLine = `{ ${join(fields, ', ')} }`;
    return fieldsLine.length > MAX_LINE_LENGTH ? block(fields) : fieldsLine;
  },

  ObjectField: ({ name, value }) => `${name}: ${value}`,

  // Directive

  Directive: ({ name, arguments: args }) => {
    if (!!args && args.length === 0) {
      return `@${name}`;
    }

    return `@${name}${wrap('(\n', indent(join(args, ',\n')), '\n)')}`;
  },

  // Type

  NamedType: ({ name }) => name,
  ListType: ({ type }) => `[${type}]`,
  NonNullType: ({ type }) => `${type}!`,

  // Type System Definitions

  SchemaDefinition: ({ description, directives, operationTypes }) =>
    wrap('', description, '\n') +
    join(['schema', join(directives, ' '), block(operationTypes)], ' '),

  OperationTypeDefinition: ({ operation, type }) => `${operation}: ${type}`,

  ScalarTypeDefinition: ({ description, name, directives }) =>
    wrap('', description, '\n') +
    join(['scalar', name, join(directives, ' ')], ' '),

  ObjectTypeDefinition: ({
    description,
    name,
    interfaces,
    directives,
    fields,
  }) =>
    wrap('', description, '\n') +
    join(
      [
        'type',
        name,
        wrap('implements ', join(interfaces, ' & ')),
        join(directives, ' '),
        block(fields),
      ],
      ' ',
    ),

  FieldDefinition: ({ description, name, arguments: args, type, directives }) =>
    `${
      wrap('', description, '\n') +
      name +
      wrap('(\n', indent(join(args, '\n')), '\n)')
    }: ${type}${wrap(' ', join(directives, ' '))}`,

  InputValueDefinition: ({
    description,
    name,
    type,
    defaultValue,
    directives,
  }) =>
    wrap('', description, '\n') +
    join(
      [`${name}: ${type}`, wrap('= ', defaultValue), join(directives, ' ')],
      ' ',
    ),

  InterfaceTypeDefinition: ({
    description,
    name,
    interfaces,
    directives,
    fields,
  }) =>
    wrap('', description, '\n') +
    join(
      [
        'interface',
        name,
        wrap('implements ', join(interfaces, ' & ')),
        join(directives, ' '),
        block(fields),
      ],
      ' ',
    ),

  UnionTypeDefinition: ({ description, name, directives, types }) =>
    wrap('', description, '\n') +
    join(
      ['union', name, join(directives, ' '), wrap('= ', join(types, ' | '))],
      ' ',
    ),

  EnumTypeDefinition: ({ description, name, directives, values }) =>
    wrap('', description, '\n') +
    join(['enum', name, join(directives, ' '), block(values)], ' '),

  EnumValueDefinition: ({ description, name, directives }) =>
    wrap('', description, '\n') + join([name, join(directives, ' ')], ' '),

  InputObjectTypeDefinition: ({ description, name, directives, fields }) =>
    wrap('', description, '\n') +
    join(['input', name, join(directives, ' '), block(fields)], ' '),

  DirectiveDefinition: ({
    description,
    name,
    arguments: args,
    repeatable,
    locations,
  }) =>
    `${wrap('', description, '\n')}directive @${name}${wrap(
      '(\n',
      indent(join(args, '\n')),
      '\n)',
    )}${repeatable ? ' repeatable' : ''} on ${join(locations, ' | ')}`,

  SchemaExtension: ({ directives, operationTypes }) =>
    join(['extend schema', join(directives, ' '), block(operationTypes)], ' '),

  ScalarTypeExtension: ({ name, directives }) =>
    join(['extend scalar', name, join(directives, ' ')], ' '),

  ObjectTypeExtension: ({ name, interfaces, directives, fields }) =>
    join(
      [
        'extend type',
        name,
        wrap('implements ', join(interfaces, ' & ')),
        join(directives, ' '),
        block(fields),
      ],
      ' ',
    ),

  InterfaceTypeExtension: ({ name, interfaces, directives, fields }) =>
    join(
      [
        'extend interface',
        name,
        wrap('implements ', join(interfaces, ' & ')),
        join(directives, ' '),
        block(fields),
      ],
      ' ',
    ),

  UnionTypeExtension: ({ name, directives, types }) =>
    join(
      [
        'extend union',
        name,
        join(directives, ' '),
        wrap('= ', join(types, ' | ')),
      ],
      ' ',
    ),

  EnumTypeExtension: ({ name, directives, values }) =>
    join(['extend enum', name, join(directives, ' '), block(values)], ' '),

  InputObjectTypeExtension: ({ name, directives, fields }) =>
    join(['extend input', name, join(directives, ' '), block(fields)], ' '),
};

/**
 * Given maybeArray, print an empty string if it is null or empty, otherwise
 * print all items together separated by separator if provided
 */
function join(
  maybeArray: Maybe<ReadonlyArray<string | undefined>>,
  separator = '',
): string {
  return maybeArray?.filter((x) => x).join(separator) ?? '';
}

/**
 * Given array, print each item on its own line, wrapped in an indented `{ }` block.
 */
function block(array: Maybe<ReadonlyArray<string | undefined>>): string {
  return wrap('{\n', indent(join(array, '\n')), '\n}');
}

/**
 * If maybeString is not null or empty, then wrap with start and end, otherwise print an empty string.
 */
function wrap(
  start: string,
  maybeString: Maybe<string>,
  end: string = '',
): string {
  return typeof maybeString !== 'undefined' &&
    maybeString !== null &&
    maybeString !== ''
    ? start + maybeString + end
    : '';
}

function indent(str: string): string {
  return wrap('  ', str.replaceAll('\n', '\n  '));
}
